interface SkeletonProps {
  className?: string
  style?: React.CSSProperties
}

function Skeleton({ className = "", style }: SkeletonProps) {
  return <div className={`animate-pulse rounded bg-gold-500/30 ${className}`} style={style} />
}

export function DonorWorkspaceSkeleton() {
  return (
    <div role="status" aria-label="Loading sponsor workspace" className="flex h-screen overflow-hidden bg-cream-50">
      <aside className="hidden lg:flex w-[280px] flex-none flex-col border-r border-gold-500 bg-white p-6">
        <div className="flex flex-col gap-1">
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} className="h-12 w-full rounded-lg" style={{ animationDelay: `${index * 80}ms` }} />
          ))}
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gold-500 bg-white px-4 py-3 sm:px-5 sm:py-4 lg:px-8 lg:py-5">
          <div className="flex flex-col gap-2">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-6 w-48" />
          </div>
          <Skeleton className="h-10 w-32 flex-none rounded-full" />
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="flex w-full max-w-[960px] flex-col gap-5 px-4 py-5 sm:px-5 sm:py-6 lg:px-8 lg:py-8">
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-72" />
            </div>
            {[0, 1].map((index) => (
              <div key={index} className="flex flex-col gap-4 rounded-[10px] border border-gold-500 bg-white p-6">
                <Skeleton className="h-5 w-1/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </main>
        <div className="flex flex-none items-center justify-around border-t border-gold-500 bg-white px-2 pt-1.5 pb-2.5 lg:hidden">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="flex flex-col items-center gap-1 px-1 py-1.5">
              <Skeleton className="h-5 w-5 rounded-full" />
              <Skeleton className="h-2.5 w-12 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function DonorChildDetailSkeleton() {
  return (
    <div role="status" aria-label="Loading child profile" className="flex flex-col gap-6">
      <Skeleton className="h-4 w-40" />

      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-[auto_1fr] sm:gap-6 sm:p-8">
          <Skeleton className="h-24 w-24 rounded-[10px] sm:h-40 sm:w-40" />
          <div className="flex flex-col">
            <Skeleton className="h-7 w-56" />
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 mt-3 sm:grid-cols-4 sm:gap-y-2">
              {[0, 1, 2].map((index) => (
                <div key={index} className="flex flex-col gap-1.5">
                  <Skeleton className="h-3 w-14" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
              <div className="col-span-2 flex flex-col gap-1.5 mt-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="mt-4 h-4 w-56" />
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        {[0, 1].map((index) => (
          <div key={index} className="rounded-[10px] border border-gold-500 bg-white p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-col gap-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-56" />
              </div>
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="grid gap-3 sm:grid-cols-3 mt-4">
              {[0, 1, 2].map((tile) => (
                <Skeleton key={tile} className="h-16 rounded-lg" />
              ))}
            </div>
            <div className="flex flex-col gap-3 mt-4">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-16 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-[10px] border border-gold-500 bg-white">
        <div className="flex flex-col gap-2 border-b border-gold-500/40 p-5">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-4 w-64" />
        </div>
        {[0, 1].map((index) => (
          <div
            key={index}
            className={`flex flex-col gap-2 p-5 sm:flex-row sm:items-center sm:justify-between ${index > 0 ? "border-t border-gold-500/40" : ""}`}
          >
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-64" />
            </div>
            <Skeleton className="h-9 w-20 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}
